import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Square, Settings as SettingsIcon, Info, Search, History, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

import { BACKEND_URL, API } from '../config';

// Ganancia/pérdida real con monedas de precio muy chico (PEPE, SUPRA, etc.)
// -- antes cualquier P&L menor a 1 centavo se redondeaba a "$0.00" fijo,
// tapando una ganancia o pérdida real aunque fuera chica. Ahora muestra los
// decimales que hagan falta (hasta 8) en vez de redondear a cero.
const formatPnl = (v) => {
  const n = Number(v);
  if (!n) return '0.00';
  const abs = Math.abs(n);
  if (abs < 0.01) {
    let s = n.toFixed(8).replace(/0+$/, '');
    if (s.endsWith('.')) s += '00';
    return s;
  }
  return n.toFixed(2);
};

const BotsManagement = () => {
  const _urlParams = new URLSearchParams(window.location.search);
  const _urlSymbol = _urlParams.get('symbol');
  const _urlExchange = _urlParams.get('exchange');

  const [availableBots, setAvailableBots] = useState([]);
  const [userBotInstances, setUserBotInstances] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedBotTemplate, setSelectedBotTemplate] = useState(null);
  const [configForm, setConfigForm] = useState({
    exchange_id: '',
    symbol: 'BTC/USDT',
    market_type: 'spot',
    leverage: 1,
    trade_amount: 0.001,
    timeframe: '1h',
    monitor_interval_min: 2.5,
    usd_amount: '50',
    budget_total_usd: '500',
    safety_orders: 3,
    safety_step_pct: 3.0,
    take_profit_pct: 1.5,
    stop_loss_pct: 15.0,
    size_multiplier: 1.0,
    tu_stop_atr: 2.0,
    retroceso_max_pct: 55.0,
    umbral_base: 0.22,
    stop_loss_pct_generic: '',
    take_profit_pct_generic: '',
  });
  const [livePrice, setLivePrice] = useState(null);
  const [minOrder, setMinOrder] = useState(null);
  const [exchangeMarkets, setExchangeMarkets] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [ateneaCandidatos, setAteneaCandidatos] = useState([]);
  const [paperBal, setPaperBal] = useState(null);
  const _latestPriceSymbol = useRef(null);
  const [historyOpenFor, setHistoryOpenFor] = useState(null);
  const [botHistories, setBotHistories] = useState({});
  const [loadingHistoryFor, setLoadingHistoryFor] = useState(null);
  const [historyPrices, setHistoryPrices] = useState({});
  // Edición de Stop Loss / Take Profit de un bot YA CREADO -- pedido
  // explícito: que se pueda ajustar mientras la posición está abierta y
  // todavía no llegó a esos niveles, igual que se puede hacer en un
  // exchange, sin tener que detener/borrar el bot.
  const [editingSLTPFor, setEditingSLTPFor] = useState(null);
  const [sltpForm, setSltpForm] = useState({ stop_loss_pct: '', take_profit_pct: '' });
  const [savingSLTP, setSavingSLTP] = useState(false);

  // Precio en vivo de las posiciones ABIERTAS del historial expandido, para
  // mostrar el P&L no realizado de esa operación puntual (no solo "posición
  // abierta" sin ningún número) -- así se puede decidir si cerrarla desde ahí.
  const fetchHistoryPrices = async (trades) => {
    const abiertas = (trades || []).filter(t => t.profit_loss_usd == null && t.closed !== true);
    if (!abiertas.length) return;
    const seen = new Set();
    const combos = [];
    for (const t of abiertas) {
      const ex = t.exchange_id || 'bitunix';
      const sym = t.symbol || 'BTC/USDT';
      const key = `${ex}:${sym}`;
      if (!seen.has(key)) { seen.add(key); combos.push({ ex, sym, key }); }
    }
    const results = await Promise.all(combos.map(async ({ ex, sym, key }) => {
      try {
        const r = await axios.get(`${API}/market/ticker/${ex}`,
          { params: { symbol: sym }, withCredentials: true, timeout: 8000 });
        return r.data?.last ? { key, price: r.data.last } : null;
      } catch { return null; }
    }));
    setHistoryPrices(prev => {
      const next = { ...prev };
      results.forEach(r => { if (r) next[r.key] = r.price; });
      return next;
    });
  };

  const fetchBotHistory = async (botId) => {
    setLoadingHistoryFor(botId);
    try {
      // Antes limit:100 -- un bot activo (opera cada pocos minutos) supera
      // eso fácil, y el historial general (History.js) pide hasta 5000, así
      // que este detalle por bot mostraba menos operaciones que el general
      // y no coincidían. Mismo límite alto acá.
      const r = await axios.get(`${API}/aegis/trades`, {
        params: { bot_id: botId, limit: 5000 }, withCredentials: true });
      // Al cerrar una posición, el sistema guarda el cierre como un trade NUEVO
      // (con el P&L real) y deja el de apertura marcado closed=true pero sin su
      // propio profit_loss_usd -- esa fila de apertura huérfana no aporta nada
      // (el resultado ya está en la fila de cierre), así que no se muestra.
      // Mismo filtro que ya tiene History.js -- acá faltaba.
      // Las posiciones ABIERTAS van primero (arriba), y dentro de cada grupo
      // (abiertas / cerradas) más reciente primero -- así lo que necesita
      // atención ahora mismo no queda enterrado entre cientos de cerradas.
      const trades = (r.data.trades || [])
        .filter(t => !(t.closed && t.profit_loss_usd == null))
        .slice().sort((a, b) => {
          const aOpen = a.profit_loss_usd == null && a.closed !== true;
          const bOpen = b.profit_loss_usd == null && b.closed !== true;
          if (aOpen !== bOpen) return aOpen ? -1 : 1;
          return (b.timestamp || '').localeCompare(a.timestamp || '');
        });
      setBotHistories(prev => ({ ...prev, [botId]: trades }));
      fetchHistoryPrices(trades);
    } catch {
      setBotHistories(prev => ({ ...prev, [botId]: [] }));
    }
    setLoadingHistoryFor(null);
  };

  const toggleBotHistory = async (botId) => {
    if (historyOpenFor === botId) { setHistoryOpenFor(null); return; }
    setHistoryOpenFor(botId);
    if (botHistories[botId]) { fetchHistoryPrices(botHistories[botId]); return; }
    await fetchBotHistory(botId);
  };

  // Refresco del precio en vivo cada 5s mientras el historial de un bot
  // está expandido, para que el P&L no realizado se mantenga al día.
  useEffect(() => {
    if (!historyOpenFor) return;
    const id = setInterval(() => {
      fetchHistoryPrices(botHistories[historyOpenFor]);
    }, 5000);
    return () => clearInterval(id);
  }, [historyOpenFor, botHistories]); // eslint-disable-line

  const fetchMinOrder = async (exchange, symbol, market_type) => {
    if (!exchange || !symbol) { setMinOrder(null); return; }
    try {
      const r = await axios.get(`${API}/market/min-order/${exchange}`,
        { params: { symbol, market_type: market_type || 'spot' }, withCredentials: true });
      setMinOrder(r.data);
    } catch { setMinOrder(null); }
  };

  useEffect(() => {
    fetchMinOrder(configForm.exchange_id, configForm.symbol, configForm.market_type);
  }, [configForm.exchange_id, configForm.symbol, configForm.market_type]); // eslint-disable-line

  const cargarMercados = async (exId) => {
    if (!exId) return;
    setLoadingMarkets(true);
    try {
      const r = await axios.get(`${API}/exchanges/${exId}/markets`, { withCredentials: true });
      const raw = r.data.markets || [];
      const symbols = raw.map(m => typeof m === 'string' ? m : (m.symbol || '')).filter(s => s.endsWith('/USDT') && !s.includes(':'));
      setExchangeMarkets(symbols);
    } catch { setExchangeMarkets([]); }
    setLoadingMarkets(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Refresco periódico de la lista de instancias -- antes solo cargaba una
  // vez al montar, así que un cambio de símbolo en vivo (Atenea cambiando
  // de cripto) no se veía hasta recargar a mano. Mismo patrón que ya usa
  // el refresco de precio en vivo del historial expandido (20s, liviano:
  // GET /api/bots es una lectura simple de Mongo).
  useEffect(() => {
    const id = setInterval(loadData, 20000);
    return () => clearInterval(id);
  }, []);

  // Atenea muestra su historial de operaciones EN VIVO directo en la
  // tarjeta (no detrás del botón "Historial" como el resto de los bots) --
  // se carga sola apenas aparece una instancia, y se refresca junto con el
  // resto de la lista de instancias.
  useEffect(() => {
    userBotInstances
      .filter(i => i.bot_template_id === 'atenea_trader')
      .forEach(i => { if (!botHistories[i.bot_id]) fetchBotHistory(i.bot_id); });
  }, [userBotInstances]); // eslint-disable-line

  const cargarPaper = async () => {
    try {
      const r = await axios.get(`${API}/paper/balance`, { withCredentials: true });
      setPaperBal(r.data);
    } catch {}
  };

  useEffect(() => { const t = setTimeout(cargarPaper, 800); return () => clearTimeout(t); }, []);

  const loadData = async () => {
    try {
      const [botsRes, instancesRes, exchangesRes, approvedRes] = await Promise.all([
        axios.get(`${API}/bots/available`, { withCredentials: true }),
        axios.get(`${API}/bots`, { withCredentials: true }),
        axios.get(`${API}/exchanges/keys`, { withCredentials: true }),
        axios.get(`${API}/custom-strategy/approved`, { withCredentials: true }).catch(() => ({ data: { strategies: [] } })),
      ]);

      const customTemplates = (approvedRes.data.strategies || []).map(s => ({
        id: s.bot_template_id,
        bot_id: s.bot_template_id,
        name: `Estrategia Propia: ${s.description.slice(0, 50)}${s.description.length > 50 ? '...' : ''}`,
        description: `Estrategia personalizada aprobada. Efectividad: ${s.efectividad}%\n"${s.description}"`,
        type: 'both',
        risk_level: 'medium',
        features: ['Estrategia personalizada', `Efectividad: ${s.efectividad}%`, 'Aprobada por admin',
                   'Seguro de ganancia con pico confirmado y piso que sube y nunca baja'],
        internal_only: false,
      }));
      setAvailableBots([...(botsRes.data.bots || []).filter(b => !b.internal_only), ...customTemplates]);
      setUserBotInstances(instancesRes.data.bots);
      setExchanges(exchangesRes.data.keys || []);
    } catch (error) {
      toast.error('Error al cargar bots');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureBot = async (botTemplate) => {
    setSelectedBotTemplate(botTemplate);
    const fromUrl = _urlExchange && exchanges.find(k => k.exchange_id === _urlExchange);
    const exId = fromUrl ? _urlExchange : (exchanges[0]?.exchange_id || '');
    // Aegis IA solo opera BTC — los demás usan el símbolo de la URL o BTC por defecto
    const isAegisIA = botTemplate?.id === 'aegis_ia';
    const isAtenea = botTemplate?.id === 'atenea_trader';
    const sym = isAegisIA ? 'BTC/USDT' : (_urlSymbol || 'BTC/USDT');
    // Una plantilla declarada SOLO FUTUROS (Atenea, C1) no puede arrancar el
    // formulario en 'spot': quedaba pidiendo el precio del mercado equivocado
    // y, si el usuario no lo cambiaba a mano, creaba el bot en spot.
    const mkt = botTemplate?.type === 'futures' ? 'futures' : 'spot';
    setConfigForm({
      exchange_id: exId, symbol: sym, market_type: mkt,
      // Atenea elige ella misma cuándo mirar el mercado (monitorea todo el
      // tiempo) -- se fuerza el intervalo más corto en vez de pedírselo al
      // usuario, ver bloque "Monitoreo del mercado" más abajo.
      leverage: 1, trade_amount: 0.001, timeframe: '1h', monitor_interval_min: isAtenea ? 1 : 2.5, usd_amount: '50',
      budget_total_usd: '500',
      safety_orders: 3, safety_step_pct: 3.0, take_profit_pct: 1.5,
      stop_loss_pct: 15.0, size_multiplier: 1.0,
      tu_stop_atr: 2.0, retroceso_max_pct: 55.0, umbral_base: 0.22,
      stop_loss_pct_generic: '', take_profit_pct_generic: '',
    });
    setSymbolSearch('');
    setShowConfigDialog(true);
    cargarMercados(exId);
    _latestPriceSymbol.current = sym;
    if (isAtenea) {
      try {
        const r = await axios.get(`${API}/bots/atenea-candidatos`, { withCredentials: true });
        setAteneaCandidatos(r.data.candidatos || []);
      } catch { setAteneaCandidatos([]); }
    }
    try {
      // market_type: sin esto el backend preguntaba siempre por spot, y los
      // pares de futuros quedaban sin precio (CoinEx devolvía "market BTCUSDT
      // not found" y la calculadora no podía convertir).
      const r = await axios.get(`${API}/market/ticker/${exId}`, {
        params: { symbol: sym, market_type: mkt }, withCredentials: true });
      if (_latestPriceSymbol.current === sym) setLivePrice(r.data?.last || null);
    } catch { if (_latestPriceSymbol.current === sym) setLivePrice(null); }
  };

  const handleActivateBot = async (e) => {
    e.preventDefault();
    
    // Verificar límite de 20 instancias activas
    if (userBotInstances.length >= 20) {
      toast.error('Máximo 20 instancias de bots permitidas');
      return;
    }

    try {
      await axios.post(`${API}/bots`, {
        name: selectedBotTemplate.name,
        bot_template_id: selectedBotTemplate.bot_id,
        exchange_id: configForm.exchange_id,
        symbol: configForm.symbol,
        // El tipo de la plantilla manda: si declara spot o futuros, no se
        // puede guardar lo contrario aunque el formulario tuviera otro valor.
        market_type: (selectedBotTemplate?.type === 'futures' ? 'futures'
                      : selectedBotTemplate?.type === 'spot' ? 'spot'
                      : configForm.market_type),
        leverage: configForm.leverage,
        trade_amount: parseFloat(configForm.trade_amount) || 0.001,
        // Presupuesto TOTAL (el techo -- si se pierde todo, el bot se para)
        // y monto POR CICLO (cuánto arriesga en cada operación) son dos
        // números distintos -- antes compartían sin querer el mismo campo
        // (usd_amount), así que nunca se podían configurar por separado
        // (ej. $500 de presupuesto con $100 por operación).
        budget_usd: parseFloat(configForm.budget_total_usd) || null,
        trade_amount_usd: parseFloat(configForm.usd_amount) || null,
        strategy_params: {
          // Los bots con escáner analizan siempre en 1h -- se guarda ese valor
          // para que la configuración refleje lo que de verdad hacen.
          timeframe: (['atenea_trader', 'nova', 'c1_scanner'].includes(selectedBotTemplate?.id)
                      ? '1h' : configForm.timeframe),
          monitor_interval_min: configForm.monitor_interval_min || 2.5,
          ...(selectedBotTemplate?.id === 'grid_safety_orders' ? {
            safety_orders: parseInt(configForm.safety_orders),
            safety_step_pct: parseFloat(configForm.safety_step_pct),
            take_profit_pct: parseFloat(configForm.take_profit_pct),
            stop_loss_pct: parseFloat(configForm.stop_loss_pct),
            size_multiplier: parseFloat(configForm.size_multiplier),
          } : {}),
          ...(selectedBotTemplate?.id === 'aegis_dual' || selectedBotTemplate?.id === 'nova' ? {
            tu_stop_atr: parseFloat(configForm.tu_stop_atr),
            retroceso_max_pct: parseFloat(configForm.retroceso_max_pct),
            umbral_base: parseFloat(configForm.umbral_base),
          } : {}),
          // SL/TP genérico por %% -- disponible para CUALQUIER bot que no
          // tenga ya su propia gestión de posición (Argos y Aegis Dual
          // quedan afuera porque ya tienen la suya). Opcional: si el campo
          // queda vacío, el bot sigue solo con la protección adaptativa
          // por ATR de siempre, sin cambio de comportamiento.
          ...(selectedBotTemplate?.id !== 'grid_safety_orders' && selectedBotTemplate?.id !== 'aegis_dual' ? {
            ...(configForm.stop_loss_pct_generic !== '' ? { stop_loss_pct: parseFloat(configForm.stop_loss_pct_generic) } : {}),
            ...(configForm.take_profit_pct_generic !== '' ? { take_profit_pct: parseFloat(configForm.take_profit_pct_generic) } : {}),
          } : {}),
        }
      }, { withCredentials: true });
      
      toast.success(`Bot "${selectedBotTemplate.name}" activado`);
      setShowConfigDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar bot');
    }
  };

  const handleStartBot = async (botId) => {
    try {
      await axios.post(`${API}/bots/${botId}/start`, {}, { withCredentials: true });
      toast.success('Bot iniciado');
      loadData();
    } catch (error) {
      toast.error('Error al iniciar bot');
    }
  };

  const handleStopBot = async (botId) => {
    try {
      await axios.post(`${API}/bots/${botId}/stop`, {}, { withCredentials: true });
      toast.success('Bot detenido');
      loadData();
    } catch (error) {
      toast.error('Error al detener bot');
    }
  };

  const handleClosePosition = async (tradeId, botId) => {
    if (!window.confirm('¿Cerrar esta posición al precio actual?')) return;
    try {
      const r = await axios.post(`${API}/aegis/trades/${tradeId}/close`, {}, { withCredentials: true });
      toast.success(`Cerrada: P&L $${r.data.pnl?.toFixed(2) ?? '0.00'}`);
      loadData();
      if (botId) fetchBotHistory(botId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar posición');
    }
  };

  // "Detener" solo pausa el bot -- esto es lo que cierra TODAS las
  // posiciones abiertas del bot, sin pausarlo. Antes el botón de afuera
  // (open_position_detail) solo cerraba la más reciente aunque hubiera
  // varias patas abiertas (Safety Orders de Argos) -- pedido explícito:
  // que cierre todas, y que "detener" sea una acción aparte.
  const handleCloseAllPositions = async (botId) => {
    if (!window.confirm('¿Cerrar TODAS las posiciones abiertas de este bot?')) return;
    try {
      const r = await axios.post(`${API}/bots/${botId}/close-all-positions`, {}, { withCredentials: true });
      toast.success(r.data.message);
      loadData();
      fetchBotHistory(botId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar posiciones');
    }
  };

  const handleDeleteBot = async (botId) => {
    if (!window.confirm('¿Desactivar esta instancia del bot?')) return;
    try {
      await axios.delete(`${API}/bots/${botId}`, { withCredentials: true });
      toast.success('Bot desactivado');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar bot');
    }
  };

  const openEditSLTP = (instance) => {
    const p = instance.strategy_params || {};
    const esArgos = instance.bot_template_id === 'grid_safety_orders';
    setSltpForm({
      stop_loss_pct: p.stop_loss_pct ?? (esArgos ? 10.0 : ''),
      take_profit_pct: p.take_profit_pct ?? (esArgos ? 1.0 : ''),
    });
    setEditingSLTPFor(instance.bot_id);
  };

  const handleSaveSLTP = async (instance) => {
    const esArgos = instance.bot_template_id === 'grid_safety_orders';
    setSavingSLTP(true);
    try {
      const strategy_params = {};
      if (esArgos) {
        // Argos SIEMPRE necesita un número acá (no existe "sin fijar" para
        // este bot -- son parte de su propia gestión de posición, igual
        // rango que al crearlo).
        const sl = Math.min(30, Math.max(5, parseFloat(sltpForm.stop_loss_pct) || 10));
        const tp = Math.min(10, Math.max(0.5, parseFloat(sltpForm.take_profit_pct) || 1));
        strategy_params.stop_loss_pct = sl;
        strategy_params.take_profit_pct = tp;
      } else {
        // Bots genéricos: vacío = sin fijar (deja el bot con solo la
        // protección automática por ATR) -- mismo criterio que al crearlo.
        // null lo borra del strategy_params en vez de mandar un número inválido.
        strategy_params.stop_loss_pct = sltpForm.stop_loss_pct === '' ? null : parseFloat(sltpForm.stop_loss_pct);
        strategy_params.take_profit_pct = sltpForm.take_profit_pct === '' ? null : parseFloat(sltpForm.take_profit_pct);
      }
      await axios.patch(`${API}/bots/${instance.bot_id}`, { strategy_params }, { withCredentials: true });
      toast.success('Stop Loss / Take Profit actualizado -- se aplica de inmediato, sin reiniciar el bot');
      setEditingSLTPFor(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar Stop Loss / Take Profit');
    }
    setSavingSLTP(false);
  };

  const getBotInstance = (templateId) => {
    return userBotInstances.filter(b => b.bot_template_id === templateId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="bots-management-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Gestión de Bots de Trading</h1>
          <p className="text-[#A1A1AA] mt-1">
            Tienes {userBotInstances.length}/20 instancias activas | 
            {userBotInstances.filter(b => b.status === 'running').length} ejecutándose
          </p>
        </div>

        {/* Instancias Activas -- Atenea vive en su propia página dedicada
            (menú "Atenea Trader"), no se muestra acá para no duplicar */}
        {userBotInstances.filter(b => b.bot_template_id !== 'atenea_trader').length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Instancias Activas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userBotInstances.filter(b => b.bot_template_id !== 'atenea_trader').map((instance) => {
              const isAtenea = false;
              const esNova = instance.bot_template_id === 'nova';
              // Bots con escáner: no tienen par fijo, eligen la cripto solos y
              // rotan. Mostrarles "Par: BTC/USDT" como a un bot normal era
              // engañoso -- ese valor es solo la última cripto elegida (o la
              // inicial, si todavía no rotó). Guardan sus candidatas en su
              // propio campo, que hasta ahora no se mostraba en esta lista.
              const esC1 = instance.bot_template_id === 'c1_scanner';
              const esEscaner = esNova || esC1;
              const candEscaner = instance.last_nova_candidates || instance.last_c1_candidates || [];
              const switchEscaner = instance.last_nova_switch_at || instance.last_c1_switch_at;
              // La tarjeta de Nova tiene fondo oscuro propio; el resto usa el
              // tema claro. Los colores del gráfico son SVG inline, así que
              // theme-light.css no los alcanza -- por eso los nombres de las
              // criptos quedaban en blanco sobre fondo blanco, ilegibles.
              const ejeColor = esNova ? '#d4d4d8' : '#3f3f46';
              // BUG encontrado y corregido: se pintaba de azul ("operando") la
              // cripto igual a `instance.symbol`, que en un bot con escáner es
              // solo LA ÚLTIMA ELEGIDA -- el bot puede estar plano hace horas y
              // esa cripto seguía marcada como si tuviera una posición abierta.
              // Confirmado en producción: C1 mostraba TRX/USDT en azul con la
              // posición ya cerrada a mano y nada abierto. Ahora "operando"
              // sale del símbolo de la posición REAL que devuelve el backend, y
              // la elegida-sin-abrir tiene su propio color, que es otra cosa.
              const symOperando = (instance.open_positions > 0)
                ? (instance.open_position_detail?.symbol || instance.symbol)
                : null;
              // Color por calidad de la oportunidad, no todas iguales: la que
              // se está operando se distingue, después la elegida a la espera,
              // y el resto va de verde (mejor puntaje) a gris (más flojo).
              const colorCandidata = (score, esOperando, esElegida) => {
                if (esOperando) return '#0ea5e9';
                if (esElegida) return '#a855f7';
                if (score >= 75) return '#16a34a';
                if (score >= 65) return '#65a30d';
                if (score >= 55) return '#ca8a04';
                return '#94a3b8';
              };
              return (
                <Card key={instance.bot_id}
                  className={isAtenea
                    ? "md:col-span-2 lg:col-span-3 bg-gradient-to-b from-[#14120c] to-[#0C0D10] border border-[#8a6d3b]/50"
                    : esNova
                    ? "bg-gradient-to-br from-[#0f1a17] via-[#0d1620] to-[#0c0f14] border border-[#7dd8c0]/25"
                    : "bg-[#121212] border-[#27272A]"}
                  data-testid={`bot-instance-${instance.bot_id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className={esNova ? "text-lg bg-clip-text text-transparent bg-gradient-to-r from-[#8fe3c7] to-[#8ecae6] font-bold" : "text-white text-lg"}>
                          {esNova && '✨ '}{instance.name}
                        </CardTitle>
                        <CardDescription className="uppercase text-xs">{instance.exchange_id}</CardDescription>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs uppercase ${
                        instance.status === 'running' ? (esNova ? 'bg-[#7dd8c0]/15 text-[#8fe3c7]' : 'bg-[#00FF88]/10 text-[#00FF88]') :
                        instance.status === 'error' ? 'bg-red-500/10 text-red-500' :
                        'bg-[#52525B]/10 text-[#52525B]'
                      }`}>
                        {instance.status}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-[#A1A1AA]">Exchange</p>
                        <p className="text-white uppercase text-xs font-bold">{instance.exchange_id || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">{esEscaner ? 'Operando ahora' : 'Par'}</p>
                        <p className="text-white mono">{instance.symbol}</p>
                        {esEscaner && (
                          <p className="text-[9px] text-[#52525B]">elegida entre ~40 por el escáner</p>
                        )}
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">Tipo</p>
                        <p className="text-white uppercase">{instance.market_type}</p>
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">Leverage</p>
                        <p className="text-white mono">{instance.leverage}x</p>
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">Trades</p>
                        <p className="text-white mono">{instance.total_trades}</p>
                        {(instance.open_positions || 0) > 0 && (
                          <div className="mt-0.5">
                            <p className="text-xs text-[#FFD600] font-medium">
                              {instance.open_positions} abierta{instance.open_positions > 1 ? 's' : ''}
                            </p>
                            {instance.open_position_detail && (
                              <p className="text-[10px] text-[#A1A1AA] mono leading-tight">
                                {instance.open_position_detail.direction === 'long' ? '↑ Long' : '↓ Short'} @{' '}
                                ${instance.open_position_detail.entry_price < 1
                                  ? Number(instance.open_position_detail.entry_price).toFixed(8)
                                  : Number(instance.open_position_detail.entry_price).toFixed(2)}
                                {instance.open_position_detail.paper_trading && <span className="text-[#00D4FF]"> (paper)</span>}
                              </p>
                            )}
                            {instance.open_position_detail?.trade_id && (
                              <button
                                type="button"
                                onClick={() => handleCloseAllPositions(instance.bot_id)}
                                className="mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/20 transition-all"
                              >
                                {instance.open_positions > 1 ? `Cerrar todas (${instance.open_positions})` : 'Cerrar posición'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {instance.budget_usd > 0 && (
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-[#A1A1AA]">Presupuesto</span>
                        <span className="text-white mono">
                          ${Math.max(0, instance.budget_usd + (instance.total_profit_usd || 0)).toFixed(2)}
                          <span className="text-[#52525B]"> / ${instance.budget_usd.toFixed(2)}</span>
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#1E1E1E] p-2 rounded">
                        <p className="text-xs text-[#A1A1AA]">P&L</p>
                        <p className={`text-lg font-bold mono ${instance.total_profit_usd >= 0 ? 'text-positive' : 'text-negative'}`}>
                          ${instance.total_profit_usd.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-[#1E1E1E] p-2 rounded">
                        <p className="text-xs text-[#A1A1AA]">Ultima accion</p>
                        <p className={`text-sm font-bold ${instance.last_action === 'buy' ? 'text-[#00FF88]' : instance.last_action === 'sell' ? 'text-[#FF3B30]' : 'text-[#A1A1AA]'}`}>
                          {instance.last_action?.toUpperCase() || 'HOLD'}
                        </p>
                        {instance.last_cycle_at && <p className="text-[10px] text-[#52525B]">{new Date(instance.last_cycle_at).toLocaleTimeString()}</p>}
                      </div>
                    </div>
                    {esEscaner && (
                      <div className="space-y-3 pt-2 border-t border-[#8a6d3b]/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">
                              {symOperando ? 'Operando ahora' : 'Cripto elegida (sin posición abierta)'}
                            </p>
                            <p className="text-white font-bold mono">{symOperando || instance.symbol}</p>
                          </div>
                          {switchEscaner && (
                            <div className="text-right">
                              <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">Último cambio</p>
                              <p className="text-[11px] text-[#d4af6a] mono">{new Date(switchEscaner).toLocaleString()}</p>
                            </div>
                          )}
                        </div>

                        {candEscaner.length > 0 && (
                          <div>
                            <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide mb-1">Escáner en vivo — candidatas</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5 text-[9px]">
                              {[...(symOperando ? [['#0ea5e9', 'operando']] : []),
                                ['#a855f7', 'elegida, sin abrir'], ['#16a34a', 'muy buena'],
                                ['#65a30d', 'buena'], ['#ca8a04', 'aceptable'],
                                ['#94a3b8', 'floja']].map(([c, t]) => (
                                <span key={t} className="flex items-center gap-1">
                                  <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: c }} />
                                  <span style={{ color: ejeColor }}>{t}</span>
                                </span>
                              ))}
                            </div>
                            <ResponsiveContainer width="100%" height={Math.max(140, candEscaner.length * 18)}>
                              <BarChart data={candEscaner} layout="vertical" margin={{ left: 70, top: 4, right: 16, bottom: 4 }}>
                                <XAxis type="number" domain={[0, 100]} tick={{ fill: ejeColor, fontSize: 10 }} />
                                <YAxis type="category" dataKey="symbol" tick={{ fill: ejeColor, fontSize: 10 }} width={65} />
                                <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }}
                                  formatter={(v) => [v, 'Score']} />
                                <Bar dataKey="score" name="Score">
                                  {candEscaner.map((c, i) => (
                                    <Cell key={i} fill={colorCandidata(
                                      c.score,
                                      symOperando != null && c.symbol === symOperando,
                                      c.symbol === instance.symbol && c.symbol !== symOperando)} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {(() => {
                          const _cierres = (botHistories[instance.bot_id] || [])
                            .filter(t => t.profit_loss_usd != null)
                            .slice()
                            .sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
                          if (_cierres.length === 0) return null;
                          let _acum = 0;
                          const _serie = _cierres.map(t => {
                            _acum += t.profit_loss_usd;
                            return { fecha: new Date(t.timestamp).toLocaleDateString(), pnl: Number(_acum.toFixed(2)) };
                          });
                          return (
                            <div>
                              <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide mb-1">P&L acumulado</p>
                              <ResponsiveContainer width="100%" height={140}>
                                <LineChart data={_serie}>
                                  <XAxis dataKey="fecha" tick={{ fill: '#A1A1AA', fontSize: 9 }} />
                                  <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                                  <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }}
                                    formatter={(v) => [`$${v}`, 'Acumulado']} />
                                  <Line type="monotone" dataKey="pnl" stroke="#d4af6a" strokeWidth={2} dot={false} />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {instance.last_error && (
                      <p className="text-[10px] text-red-400 bg-red-500/5 rounded p-1">{instance.last_error}</p>
                    )}

                    {/* Editar Stop Loss / Take Profit con el bot corriendo y la
                        posición abierta -- pedido explícito: que se pueda
                        ajustar sin esperar a tocar esos niveles, igual que en
                        un exchange. Aegis Dual queda afuera: su protección es
                        por ATR/retroceso, no tiene un "%% fijo" equivalente. */}
                    {instance.bot_template_id !== 'aegis_dual' && (
                      editingSLTPFor === instance.bot_id ? (
                        <div className="bg-[#1E1E1E] p-2 rounded border border-[#8a6d3b]/30 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px]">Stop Loss %</Label>
                              <Input type="number" min={instance.bot_template_id === 'grid_safety_orders' ? 5 : 0}
                                max={instance.bot_template_id === 'grid_safety_orders' ? 30 : undefined} step="0.5"
                                placeholder={instance.bot_template_id === 'grid_safety_orders' ? '' : 'Sin fijar'}
                                value={sltpForm.stop_loss_pct}
                                onChange={(e) => setSltpForm({ ...sltpForm, stop_loss_pct: e.target.value })}
                                className="bg-[#111] border-[#27272A] text-white text-sm mt-1 h-8" />
                            </div>
                            <div>
                              <Label className="text-[10px]">Take Profit %</Label>
                              <Input type="number" min={instance.bot_template_id === 'grid_safety_orders' ? 0.5 : 0}
                                max={instance.bot_template_id === 'grid_safety_orders' ? 10 : undefined} step="0.5"
                                placeholder={instance.bot_template_id === 'grid_safety_orders' ? '' : 'Sin fijar'}
                                value={sltpForm.take_profit_pct}
                                onChange={(e) => setSltpForm({ ...sltpForm, take_profit_pct: e.target.value })}
                                className="bg-[#111] border-[#27272A] text-white text-sm mt-1 h-8" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleSaveSLTP(instance)} disabled={savingSLTP}
                              className="flex-1 h-7 text-xs bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 hover:bg-[#00FF88]/20">
                              Guardar
                            </Button>
                            <Button onClick={() => setEditingSLTPFor(null)} variant="outline"
                              className="h-7 text-xs border-[#27272A]">
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button onClick={() => openEditSLTP(instance)} variant="outline"
                          className="w-full h-7 text-xs border-[#27272A] text-[#A1A1AA] hover:text-white">
                          Editar Stop Loss / Take Profit
                        </Button>
                      )
                    )}

                    <div className="flex gap-2">
                      {instance.status === 'running' ? (
                        <Button
                          onClick={() => handleStopBot(instance.bot_id)}
                          className="flex-1 bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20"
                          data-testid={`stop-${instance.bot_id}`}
                        >
                          <Square size={14} className="mr-1" /> Detener
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleStartBot(instance.bot_id)}
                          className="flex-1 bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 hover:bg-[#00FF88]/20"
                          data-testid={`start-${instance.bot_id}`}
                        >
                          <Play size={14} className="mr-1" /> Iniciar
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDeleteBot(instance.bot_id)}
                        variant="outline"
                        className="border-[#27272A]"
                        data-testid={`delete-${instance.bot_id}`}
                      >
                        Desactivar
                      </Button>
                    </div>

                    <Button
                      onClick={() => toggleBotHistory(instance.bot_id)}
                      variant="outline"
                      className="w-full border-[#27272A] text-[#A1A1AA] hover:text-white"
                      data-testid={`history-${instance.bot_id}`}
                    >
                      <History size={14} className="mr-1.5" />
                      Historial de este bot
                      {historyOpenFor === instance.bot_id
                        ? <ChevronUp size={14} className="ml-1.5" />
                        : <ChevronDown size={14} className="ml-1.5" />}
                    </Button>

                    {historyOpenFor === instance.bot_id && (
                      <div className="bg-[#1E1E1E] border border-[#27272A] rounded p-2 max-h-72 overflow-y-auto">
                        {loadingHistoryFor === instance.bot_id ? (
                          <p className="text-xs text-[#A1A1AA] text-center py-3">Cargando...</p>
                        ) : (botHistories[instance.bot_id] || []).length === 0 ? (
                          <p className="text-xs text-[#A1A1AA] text-center py-3">Sin operaciones todavía</p>
                        ) : (
                          <div className="space-y-1.5">
                            {(() => {
                              const _cierres = (botHistories[instance.bot_id] || []).filter(t => t.profit_loss_usd != null);
                              if (_cierres.length === 0) return null;
                              const _total = _cierres.reduce((s, t) => s + t.profit_loss_usd, 0);
                              return (
                                <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-[#27272A]">
                                  <span className="text-[10px] text-[#A1A1AA]">Total ({_cierres.length} cerradas)</span>
                                  <span className={`text-xs font-bold mono ${_total >= 0 ? 'text-positive' : 'text-negative'}`}>
                                    {_total >= 0 ? '+' : ''}${_total.toFixed(2)}
                                  </span>
                                </div>
                              );
                            })()}
                            {(botHistories[instance.bot_id] || []).map((t) => {
                              const fmtPx = (p) => p == null ? '-' : Number(p).toLocaleString(undefined, { maximumFractionDigits: p < 1 ? 8 : 2 });
                              const esCierre = t.profit_loss_usd != null;
                              // closed=true sin P&L: registro viejo que quedó huérfano
                              // (previo a la corrección del bug de posiciones fantasma).
                              // No es una posición realmente abierta — no mostrarla como tal.
                              const realmenteAbierta = !esCierre && t.closed !== true;
                              // Si esta fila cierra una posicion (tiene entry_price), la
                              // "Apertura" es el precio de la accion CONTRARIA a t.action
                              // (la que abrio) — mostrar esa, no la de esta fila (que cierra).
                              const tieneParEntrada = esCierre && t.entry_price != null;
                              const accionAperturaEsBuy = tieneParEntrada ? t.action !== 'buy' : t.action === 'buy';
                              return (
                                <div key={t.trade_id} className="text-[11px] py-1.5 border-b border-[#27272A] last:border-0">
                                  <div className="flex items-center justify-between">
                                    <span>
                                      <span className={`font-bold mr-1 ${accionAperturaEsBuy ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                                        {accionAperturaEsBuy ? 'BUY' : 'SELL'}
                                      </span>
                                      <span className="text-white mono">{t.symbol}</span>
                                    </span>
                                    <span className={`px-1 rounded text-[9px] font-bold ${t.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                                      {t.paper_trading ? 'PAPER' : 'REAL'}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between mt-0.5">
                                    <span className="text-[#A1A1AA] mono">
                                      {esCierre && t.entry_price != null ? (
                                        <>Apertura: ${fmtPx(t.entry_price)} → Cierre: ${fmtPx(t.price)}</>
                                      ) : (
                                        <>Apertura: ${fmtPx(t.price)}</>
                                      )}
                                      <span className="text-[#52525B] ml-1">× {t.amount}</span>
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between mt-0.5">
                                    {esCierre ? (
                                      <span className={`mono font-bold ${t.profit_loss_usd > 0 ? 'text-positive' : t.profit_loss_usd < 0 ? 'text-negative' : ''}`}>
                                        {t.profit_loss_usd > 0 ? '+' : ''}${formatPnl(t.profit_loss_usd)}
                                      </span>
                                    ) : realmenteAbierta ? (() => {
                                      const _key = `${t.exchange_id || 'bitunix'}:${t.symbol || 'BTC/USDT'}`;
                                      const _now = historyPrices[_key];
                                      if (!_now || !t.price) {
                                        return <span className="text-[#52525B]">posición abierta</span>;
                                      }
                                      const _qty = t.amount || 0;
                                      const _lev = t.leverage || 1;
                                      const _isLong = t.action === 'buy';
                                      const _delta = _isLong ? (_now - t.price) : (t.price - _now);
                                      const _feePct = t.market_type === 'futures' ? 0.0006 : 0.002;
                                      const _fees = (t.price + _now) * _qty * _feePct;
                                      const _unreal = _delta * _qty * _lev - _fees;
                                      return (
                                        <span className={`mono font-bold ${_unreal > 0 ? 'text-positive' : _unreal < 0 ? 'text-negative' : ''}`}>
                                          {_unreal > 0 ? '+' : ''}${formatPnl(_unreal)} <span className="text-[9px] text-[#52525B] font-normal">(en vivo)</span>
                                        </span>
                                      );
                                    })() : (
                                      <span className="text-[10px] text-[#52525B]">sin P&L registrado</span>
                                    )}
                                    <span className="text-[10px] text-[#52525B]">
                                      {t.timestamp ? new Date(t.timestamp).toLocaleString() : ''}
                                    </span>
                                  </div>
                                  {realmenteAbierta && (
                                    <button
                                      type="button"
                                      onClick={() => handleClosePosition(t.trade_id, instance.bot_id)}
                                      className="w-full mt-1 py-1 rounded text-[10px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/20 transition-all"
                                    >
                                      Cerrar
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );})}
            </div>
          </div>
        )}

        {/* 20 Bots Disponibles */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Disponibles (22: Aegis IA (modelo IA) + Auto + 20 estrategias)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {availableBots.map((bot) => {
              const instances = getBotInstance(bot.bot_id);
              const isAvailable = bot.available;
              const esNovaTpl = bot.bot_id === 'nova';

              return (
                <Card
                  key={bot.bot_id}
                  className={esNovaTpl
                    ? "bg-gradient-to-br from-[#0f1a17] via-[#0d1620] to-[#0c0f14] border border-[#7dd8c0]/25 hover:border-[#7dd8c0]/50 transition-colors"
                    : "bg-[#121212] border-[#27272A] hover:border-[#3F3F46] transition-colors"}
                  data-testid={`bot-template-${bot.bot_id}`}
                >
                  <CardContent className="p-4">
                    <div className="text-center mb-3">
                      <div className="text-3xl mb-1">{esNovaTpl ? '✨' : '🤖'}</div>
                      <p className={esNovaTpl
                        ? "font-bold text-sm bg-clip-text text-transparent bg-gradient-to-r from-[#8fe3c7] to-[#8ecae6]"
                        : "text-white font-bold text-sm"}>{bot.name}</p>
                    </div>
                    <p className="text-[#A1A1AA] text-xs text-center min-h-[36px] mb-3">
                      {bot.description || 'Bot de trading automatizado'}
                    </p>
                    {instances.length > 0 && (
                      <p className="text-xs text-[#00FF88] text-center mb-2">
                        {instances.length} instancia{instances.length > 1 ? 's' : ''} activa{instances.length > 1 ? 's' : ''}
                      </p>
                    )}
                    {bot.internal_only ? (
                      <div className="w-full py-2 rounded-lg text-xs font-bold text-center bg-[#1E1E1E] border border-[#27272A] text-[#A1A1AA]">
                        🧠 Uso exclusivo de Atenea IA
                      </div>
                    ) : (
                      <button
                        onClick={() => handleConfigureBot(bot)}
                        disabled={userBotInstances.length >= 20}
                        className="w-full py-2 rounded-lg text-xs font-bold bg-[#e8f5f0] border border-[#c5e4d9] text-[#2d7a5f] hover:bg-[#d4ede3] active:scale-[0.97] transition-all disabled:opacity-50"
                        data-testid={`activate-${bot.bot_id}`}
                      >
                        Activar Bot
                      </button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Nota importante */}
        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Info className="text-[#00D4FF]" size={20} />
              <CardTitle className="text-white text-lg">Instalación de Bots</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-[#A1A1AA] text-sm space-y-2">
            <div className="bg-[#111] rounded p-3 mb-2">
              <p className="text-white font-medium mb-1">🧠 Dale TU estrategia a la IA (te dice si es factible y su % de efectividad)</p>
              <textarea id="miEstrategia" rows={2} placeholder="Ej: comprar cuando RSI < 30 y MACD cruce arriba, vender cuando RSI > 70"
                className="w-full bg-black border border-[#27272A] rounded p-2 text-white text-xs" />
              <button onClick={async () => {
                try {
                  const txt = document.getElementById('miEstrategia').value;
                  const keys = await axios.get(`${API}/exchanges/keys`, { withCredentials: true });
                  const ex = keys.data.keys?.[0]?.exchange_id;
                  if (!ex) return alert('Conecta primero un exchange en Configuración');
                  const r = await axios.post(`${API}/aegis/custom-strategy?description=${encodeURIComponent(txt)}&exchange_id=${ex}&save=true`, {}, { withCredentials: true });
                  const d = r.data;
                  alert(`${d.factible ? 'FACTIBLE ✔' : 'NO FACTIBLE'}\nEfectividad: ${d.efectividad_pct}%\n${d.veredicto}\n${d.uso || ''}`);
                } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
              }} className="mt-2 px-3 py-1 rounded bg-[#00FF88] text-black text-xs font-bold">Evaluar con la IA</button>
              <div className="flex flex-wrap gap-2 mt-2 items-center text-xs">
                <span className="text-[#A1A1AA]">Saldo a operar:</span>
                <input id="csAmount" type="number" step="0.001" defaultValue="0.001"
                  className="w-24 bg-black border border-[#27272A] rounded px-2 py-1 text-white" />
                <select id="csMarket" className="bg-black border border-[#27272A] rounded px-2 py-1 text-white">
                  <option value="spot">Spot</option>
                  <option value="futures">Futuros</option>
                </select>
                <button onClick={async () => {
                  try {
                    const txt = document.getElementById('miEstrategia').value;
                    if (!txt.trim()) return alert('Escribe tu estrategia primero');
                    const keys = await axios.get(`${API}/exchanges/keys`, { withCredentials: true });
                    const ex = keys.data.keys?.[0]?.exchange_id;
                    if (!ex) return alert('Conecta primero un exchange en Configuración');
                    const r = await axios.post(`${API}/aegis/custom-strategy?description=${encodeURIComponent(txt)}&exchange_id=${ex}&save=true`, {}, { withCredentials: true });
                    const d = r.data;
                    if (!d.strategy_id) {
                      alert(`NO FACTIBLE\nEfectividad: ${d.efectividad_pct}%\n${d.veredicto}\n\nLas condiciones no generaron señales válidas. Revisa la estrategia.`);
                      return;
                    }
                    const confirmMsg = d.factible
                      ? `FACTIBLE ✔\nEfectividad: ${d.efectividad_pct}%\n${d.veredicto}\n\n¿Solicitar autorización al admin para usarla?`
                      : `POCO FACTIBLE\nEfectividad: ${d.efectividad_pct}%\n${d.veredicto}\n\n¿Enviar al admin para que la revise igualmente?`;
                    if (window.confirm(confirmMsg)) {
                      await axios.post(`${API}/custom-strategy/request?strategy_id=${d.strategy_id}&description=${encodeURIComponent(txt)}&efectividad=${d.efectividad_pct}`, {}, { withCredentials: true });
                      alert('Solicitud enviada. El admin la revisará y te notificará.\nCuando la apruebe, aparecerá como bot disponible en esta misma página.');
                    }
                  } catch (e) { alert(e.response?.data?.detail || e.message); }
                }} className="px-3 py-1 rounded bg-[#00D4FF] text-black font-bold">Evaluar y solicitar</button>
                <button onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/atenea/chat?message=${encodeURIComponent('¿cómo está el mercado?')}`, {}, { withCredentials: true });
                    alert(r.data.response);
                  } catch (e) { alert('Conecta un exchange primero'); }
                }} className="px-3 py-1 rounded bg-emerald-600 text-white font-bold">¿Cómo está el mercado?</button>
                {paperBal && (
                  <span className="text-[#00FF88] font-bold ml-auto">
                    Saldo ficticio: ${paperBal.saldo_actual.toLocaleString()} USDT
                  </span>
                )}
              </div>
              <p className="text-xs mt-1">También puedes pedírselo al chat de Atenea: "mi estrategia: ..."</p>
            </div>
            <p>Las <span className="text-[#00D4FF] font-medium">20 estrategias</span> vienen integradas en el backend (<code className="mono text-[#00D4FF]">backend/bots/strategies.py</code>) junto al modelo de IA <span className="text-[#00FF88] font-medium">Aegis IA</span> (red neuronal + aprendizaje por refuerzo).</p>
            <p>Crea un bot, elige su estrategia, símbolo y timeframe, y pulsa <span className="text-white">Iniciar</span>: el worker lo ejecutará automáticamente 24/7 mientras el servidor esté encendido.</p>
            <p className="text-white font-medium mt-4">🛡️ Todos los bots nacen en modo Paper Trading (dinero ficticio). Actívalos en real solo cuando hayas validado sus resultados.</p>
          </CardContent>
        </Card>

        {/* Dialog de configuración */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configurar {selectedBotTemplate?.name}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleActivateBot} className="space-y-4">
              <div>
                <Label>Exchange</Label>
                <Select value={configForm.exchange_id} onValueChange={(v) => { setConfigForm({...configForm, exchange_id: v}); cargarMercados(v); }}>
                  <SelectTrigger data-testid="config-exchange-select">
                    <SelectValue placeholder="Selecciona exchange" />
                  </SelectTrigger>
                  <SelectContent>
                    {exchanges.map((ex) => (
                      <SelectItem key={ex.key_id} value={ex.exchange_id}>
                        {ex.exchange_id.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Par de Trading</Label>
                {/* Bots con escáner: no operan un par fijo, barren la lista de
                    criptos y eligen solos. Mostrarles un selector de par era
                    engañoso -- el valor elegido no cambiaba nada, porque el
                    propio bot lo sobreescribe al rotar de símbolo. */}
                {['atenea_trader', 'nova', 'c1_scanner'].includes(selectedBotTemplate?.id) ? (
                  <div className="mt-1">
                    <p className="text-[10px] text-[#d4af6a] mb-1.5">
                      No elegís un par fijo — {selectedBotTemplate?.name || 'este bot'} monitorea
                      estas {ateneaCandidatos.length || '~40'} criptos todo el tiempo y opera con la que
                      tenga mejor oportunidad ahora, cambiando sola cuando conviene.
                    </p>
                    <div className="max-h-32 overflow-y-auto grid grid-cols-3 gap-1 bg-[#111] border border-[#27272A] rounded p-2">
                      {(ateneaCandidatos.length ? ateneaCandidatos : ['Cargando...']).map(s => (
                        <span key={s} className="text-[10px] text-[#A1A1AA] font-mono">{s}</span>
                      ))}
                    </div>
                  </div>
                ) : selectedBotTemplate?.id === 'aegis_ia' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input value="BTC/USDT" disabled className="bg-[#111] border-[#27272A] text-[#A1A1AA]" />
                    <span className="text-[10px] text-[#00D4FF] whitespace-nowrap">Solo BTC — modelo entrenado con BTC</span>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1 mb-1">
                      <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#52525B] pointer-events-none" />
                      <Input
                        placeholder="Buscar par (ej: ETH, SOL...)"
                        value={symbolSearch}
                        onChange={(e) => setSymbolSearch(e.target.value)}
                        className="bg-[#111] border-[#27272A] text-white pl-7 h-8 text-sm"
                      />
                    </div>
                    <Select
                      value={configForm.symbol}
                      onValueChange={async (v) => {
                        setConfigForm({...configForm, symbol: v});
                        setSymbolSearch('');
                        _latestPriceSymbol.current = v;
                        try {
                          const r = await axios.get(`${API}/market/ticker/${configForm.exchange_id}`, { params: { symbol: v, market_type: configForm.market_type }, withCredentials: true });
                          if (_latestPriceSymbol.current === v) setLivePrice(r.data?.last || null);
                        } catch { if (_latestPriceSymbol.current === v) setLivePrice(null); }
                      }}
                      disabled={loadingMarkets}
                    >
                      <SelectTrigger data-testid="config-symbol-select">
                        <SelectValue placeholder={loadingMarkets ? 'Cargando pares...' : 'Selecciona un par'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        {(exchangeMarkets.length > 0 ? exchangeMarkets : ['BTC/USDT'])
                          .filter(p => !symbolSearch || p.toLowerCase().includes(symbolSearch.toLowerCase()))
                          .map(pair => (
                            <SelectItem key={pair} value={pair}>{pair}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#52525B] mt-0.5">
                      {loadingMarkets ? 'Cargando pares del exchange...' : symbolSearch
                        ? `${(exchangeMarkets).filter(p => p.toLowerCase().includes(symbolSearch.toLowerCase())).length} coincidencias de ${exchangeMarkets.length}`
                        : `${exchangeMarkets.length} pares disponibles`}
                    </p>
                  </>
                )}
              </div>

              <div>
                <Label>Tipo de Mercado</Label>
                {/* Se respeta el tipo declarado por la plantilla. Antes el
                    selector ofrecía siempre spot y futuros sin mirarlo, y eso
                    permitió crear C1 en CoinEx SPOT, donde la comisión de ida
                    y vuelta (0.40%) supera el movimiento esperado de BTC en 1h
                    (0.345%): el bot corrió 26 días sin poder operar ni una vez,
                    porque el filtro de comisión descartaba cada señal. */}
                {selectedBotTemplate?.type === 'futures' ? (
                  <p className="text-[10px] text-[#d4af6a] mt-1">
                    Solo futuros — en spot la comisión se come el movimiento que esta estrategia busca capturar.
                  </p>
                ) : selectedBotTemplate?.type === 'spot' ? (
                  <p className="text-[10px] text-[#d4af6a] mt-1">
                    Solo spot — este bot está diseñado para operar sin apalancamiento.
                  </p>
                ) : (
                  <Select value={configForm.market_type} onValueChange={(v) => setConfigForm({...configForm, market_type: v})}>
                    <SelectTrigger data-testid="config-market-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spot">Spot</SelectItem>
                      <SelectItem value="futures">Futures</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Cantidad — aplica igual a spot y futures */}
              <div>
                <Label>Cantidad a operar por ciclo: {configForm.trade_amount} {configForm.symbol?.split('/')[0]}</Label>
                <Input type="number" step="any" min="0" value={configForm.trade_amount}
                  onChange={(e) => setConfigForm({ ...configForm, trade_amount: e.target.value })}
                  className="bg-[#111] border-[#27272A] text-white mb-1" />
                <p className="text-[10px] text-[#52525B]">
                  {configForm.market_type === 'spot' ? 'Spot: cantidad de cripto a comprar/vender por ciclo' : 'Futuros: tamaño de contrato por ciclo'}
                </p>
              </div>

              {/* Presupuesto TOTAL -- distinto de "cantidad a operar por ciclo"
                  de arriba. Antes ambos compartían sin querer el mismo campo,
                  así que nunca se podían configurar valores distintos (ej.
                  $500 de presupuesto total operando de a $100 por vez). */}
              <div>
                <Label>Presupuesto total (USD)</Label>
                <Input type="number" step="any" min="0" value={configForm.budget_total_usd}
                  onChange={(e) => setConfigForm({ ...configForm, budget_total_usd: e.target.value })}
                  placeholder="500" className="bg-[#111] border-[#27272A] text-white mb-1" />
                <p className="text-[10px] text-[#52525B]">
                  Techo total: si el bot acumula pérdidas hasta este monto, se detiene solo. No define
                  cuánto arriesga en cada operación individual -- eso lo define la cantidad por ciclo.
                </p>
              </div>

              {configForm.market_type === 'futures' && (
                <div>
                  <Label>Apalancamiento: {configForm.leverage}x</Label>
                  <Input
                    type="range" min="1" max="16" value={configForm.leverage}
                    onChange={(e) => setConfigForm({...configForm, leverage: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-[#A1A1AA]">
                    <span>1x</span><span>16x</span>
                  </div>
                  {selectedBotTemplate?.id === 'grid_safety_orders' && configForm.timeframe === '1h' && (
                    <p className="text-xs text-[#00D4FF] mt-1">
                      Referencia: 16x fue lo validado con datos reales para Argos en timeframe 1h — podés ajustarlo igual si preferís otro.
                    </p>
                  )}
                  <p className="text-[10px] text-[#52525B] mt-1">
                    Máximo 16x: por encima, el stop loss no puede proteger la cuenta antes de la liquidación del exchange.
                  </p>
                </div>
              )}

              <div>
                <Label>Timeframe</Label>
                {/* Los bots con escáner analizan SIEMPRE en 1h: sus módulos
                    piden las velas de 1h directamente (ver get_nova_action y
                    get_c1_scanner_action), así que el valor que eligiera el
                    usuario acá no cambiaba nada. */}
                {['atenea_trader', 'nova', 'c1_scanner'].includes(selectedBotTemplate?.id) ? (
                  <p className="text-[10px] text-[#52525B] mt-1">
                    Fijo en 1h — es el timeframe con el que analiza y decide, no se configura.
                  </p>
                ) : (
                  <div className="flex gap-1 mt-1">
                    {['1m','5m','15m','30m','1h','2h','4h','1d'].map(tf => (
                      <button key={tf} type="button" onClick={() => setConfigForm({...configForm, timeframe: tf})}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${configForm.timeframe === tf ? 'bg-[#00D4FF] text-black' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white'}`}>
                        {tf}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedBotTemplate?.id === 'atenea_trader' ? (
                <div>
                  <Label>Monitoreo del mercado</Label>
                  <p className="text-[10px] text-[#52525B] mt-1">
                    Lo decide Atenea — está monitoreando el mercado todo el tiempo para saber
                    cuándo entrar, salir o cambiar de cripto, sin que haga falta configurarlo.
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Monitoreo del mercado</Label>
                  <div className="flex gap-1 mt-1">
                    {[{label:'1 min',val:1},{label:'2.5 min',val:2.5},{label:'5 min',val:5},{label:'10 min',val:10}].map(opt => (
                      <button key={opt.val} type="button" onClick={() => setConfigForm({...configForm, monitor_interval_min: opt.val})}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${configForm.monitor_interval_min === opt.val ? 'bg-[#00FF88] text-black' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-1">Cada cuánto el bot revisa el mercado — independiente del timeframe de análisis</p>
                </div>
              )}

              {selectedBotTemplate?.id !== 'grid_safety_orders' && selectedBotTemplate?.id !== 'aegis_dual' && (
                <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A] space-y-3">
                  <p className="text-xs text-[#00D4FF] font-medium">Stop Loss / Take Profit (opcional)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Stop Loss %</Label>
                      <Input type="number" min="0" step="0.5" placeholder="Sin fijar"
                        value={configForm.stop_loss_pct_generic}
                        onChange={(e) => setConfigForm({...configForm, stop_loss_pct_generic: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Take Profit %</Label>
                      <Input type="number" min="0" step="0.5" placeholder="Sin fijar"
                        value={configForm.take_profit_pct_generic}
                        onChange={(e) => setConfigForm({...configForm, take_profit_pct_generic: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#52525B]">
                    Cierra la posición apenas ganes o pierdas ese % de lo invertido (ya con el
                    apalancamiento incluido, no % de precio crudo). Si se deja vacío, el bot sigue
                    protegido solo por el stop loss adaptativo automático.
                  </p>
                </div>
              )}

              {selectedBotTemplate?.id === 'grid_safety_orders' && (
                <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A] space-y-3">
                  <p className="text-xs text-[#00D4FF] font-medium">Parámetros Argos</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Safety Orders (1-5)</Label>
                      <Input type="number" min="1" max="5" step="1"
                        value={configForm.safety_orders}
                        onChange={(e) => setConfigForm({...configForm, safety_orders: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Caída entre órdenes %</Label>
                      <Input type="number" min="1.0" max="10.0" step="0.5"
                        value={configForm.safety_step_pct}
                        onChange={(e) => setConfigForm({...configForm, safety_step_pct: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Take Profit %</Label>
                      <Input type="number" min="0.5" max="10.0" step="0.5"
                        value={configForm.take_profit_pct}
                        onChange={(e) => setConfigForm({...configForm, take_profit_pct: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Stop Loss Global %</Label>
                      <Input type="number" min="5.0" max="30.0" step="1.0"
                        value={configForm.stop_loss_pct}
                        onChange={(e) => setConfigForm({...configForm, stop_loss_pct: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Multiplicador SO (tamaño relativo: 1.0 = igual)</Label>
                      <Input type="number" min="1.0" max="3.0" step="0.1"
                        value={configForm.size_multiplier}
                        onChange={(e) => setConfigForm({...configForm, size_multiplier: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#52525B]">
                    {(() => {
                      // Mismo cálculo que el backend (calcular_capital_requerido):
                      // el presupuesto TOTAL se reparte entre la orden base y todas
                      // las safety orders -- no se suma libre a partir de una base,
                      // el total es el techo, no el punto de partida.
                      const _divisor = 1 + Array.from({length: parseInt(configForm.safety_orders)}, (_, i) => Math.pow(parseFloat(configForm.size_multiplier), i + 1)).reduce((a, b) => a + b, 0);
                      const _base = parseFloat(configForm.budget_total_usd) / _divisor;
                      return `Con $${configForm.budget_total_usd} de presupuesto total: orden base ≈ $${_base.toFixed(2)}, escalando en las safety orders hasta usar el total.`;
                    })()}
                  </p>
                </div>
              )}

              {(selectedBotTemplate?.id === 'aegis_dual' || selectedBotTemplate?.id === 'nova') && (
                <div className={selectedBotTemplate?.id === 'nova'
                  ? "bg-[#0f1a16] p-3 rounded border border-[#7dd8c0]/30 space-y-3"
                  : "bg-[#1E1E1E] p-3 rounded border border-[#27272A] space-y-3"}>
                  <p className={selectedBotTemplate?.id === 'nova' ? "text-xs text-[#8ecae6] font-medium" : "text-xs text-[#00D4FF] font-medium"}>
                    Parámetros {selectedBotTemplate?.id === 'nova' ? 'Nova' : 'Aegis Dual'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Stop Loss Turtle (x ATR)</Label>
                      <Input type="number" min="1.0" max="4.0" step="0.5"
                        value={configForm.tu_stop_atr}
                        onChange={(e) => setConfigForm({...configForm, tu_stop_atr: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Bloqueo de Ganancia Híbrido %</Label>
                      <Input type="number" min="20.0" max="80.0" step="5.0"
                        value={configForm.retroceso_max_pct}
                        onChange={(e) => setConfigForm({...configForm, retroceso_max_pct: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Sensibilidad de Entrada Híbrido</Label>
                      <Input type="number" min="0.10" max="0.40" step="0.02"
                        value={configForm.umbral_base}
                        onChange={(e) => setConfigForm({...configForm, umbral_base: e.target.value})}
                        className="bg-[#111] border-[#27272A] text-white text-sm mt-1" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#52525B]">
                    Motor Turtle (tendencia confirmada): stop y riesgo propios. Motor Híbrido
                    (reversión, el resto del tiempo): bloquea ganancia si retrocede el % indicado
                    desde el pico, y su sensibilidad de entrada baja o sube el umbral de votación.
                    {selectedBotTemplate?.id === 'nova' && ' Nova aplica este mismo motor a la mejor de ~40 criptos en cada ciclo, no a un símbolo fijo.'}
                  </p>
                </div>
              )}

              <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A]">
                <p className="text-xs text-[#A1A1AA] mb-2">Calculadora USD ↔ {configForm.symbol?.split('/')[0] || 'Cripto'} (cantidad por ciclo)</p>
                <p className="text-[10px] text-[#52525B] mb-2">
                  Ayuda a convertir la "cantidad a operar por ciclo" de arriba a dólares -- no toca el presupuesto total.
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Label className="text-[10px] text-[#52525B]">USD</Label>
                    <Input type="number" step="any" value={configForm.usd_amount}
                      onChange={(e) => {
                        // BUG encontrado y corregido: si no hay precio en vivo, acá se
                        // dejaba `configForm.trade_amount` tal cual -- el campo de la
                        // cripto se quedaba con el valor ANTERIOR y parecía convertido.
                        // Confirmado en producción: 5000 USD mostraba 0,001 BTC (unos
                        // $100), que era el valor de antes. Alguien podía activar un bot
                        // creyendo que operaba 5000 y operar 100, o al revés.
                        // Ahora, sin precio, el otro campo se VACÍA y el aviso de abajo
                        // explica por qué.
                        const usd = e.target.value;
                        const n = parseFloat(usd);
                        const cripto = (livePrice && isFinite(n) && n > 0)
                          ? (n / livePrice).toFixed(8).replace(/0+$/, '').replace(/\.$/, '')
                          : '';
                        setConfigForm({...configForm, usd_amount: usd, trade_amount: cripto});
                      }} placeholder="50" className="text-sm" />
                  </div>
                  <span className="text-[#A1A1AA] text-lg mt-4">=</span>
                  <div className="flex-1">
                    <Label className="text-[10px] text-[#52525B]">{configForm.symbol?.split('/')[0] || 'Cripto'}</Label>
                    <Input type="number" step="any" value={configForm.trade_amount}
                      onChange={(e) => {
                        // Mismo problema que el campo de USD, espejado.
                        const cripto = e.target.value;
                        const n = parseFloat(cripto);
                        const usd = (livePrice && isFinite(n) && n > 0)
                          ? (n * livePrice).toFixed(2)
                          : '';
                        setConfigForm({...configForm, trade_amount: cripto, usd_amount: usd});
                      }} placeholder="0.001" className="text-sm" />
                  </div>
                </div>
                {livePrice
                  ? <p className="text-[10px] text-[#52525B] mt-1">Precio actual: ${livePrice.toLocaleString()}</p>
                  : <p className="text-[11px] text-[#f59e0b] mt-1">
                      No se pudo obtener el precio de {configForm.symbol || 'este par'} en este exchange,
                      así que la calculadora no puede convertir. Escribí la cantidad directamente en
                      {' '}{configForm.symbol?.split('/')[0] || 'cripto'}.
                    </p>}
                {minOrder?.min_amount != null && (
                  <p className="text-[10px] text-[#A1A1AA] mt-1">
                    Mínimo: <span className="text-[#00D4FF] font-mono">{minOrder.min_amount} {configForm.symbol?.split('/')[0]}</span>
                    {minOrder.min_cost_usdt != null && <span> · ~${minOrder.min_cost_usdt} USDT</span>}
                  </p>
                )}
              </div>

              <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A]">
                <p className="text-xs text-[#A1A1AA] mb-2">Parámetros de Estrategia:</p>
                <pre className="text-xs text-white mono">
                  {JSON.stringify(selectedBotTemplate?.default_params || {}, null, 2)}
                </pre>
              </div>

              <div className="flex gap-3">
                <Button type="button" onClick={() => setShowConfigDialog(false)} variant="outline" className="flex-1">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#00D4FF] text-black"
                  disabled={!configForm.exchange_id}
                  data-testid="submit-bot-config"
                >
                  Activar Bot
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BotsManagement;